import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, parse, isValid } from 'date-fns';


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toTitleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Convert moment-style format tokens to date-fns format tokens.
 * Keeps backward-compat with callers that still pass moment formats.
 */
function momentFormatToDateFns(fmt: string): string {
  return fmt
    // Literal brackets: [at] → 'at'
    .replace(/\[([^\]]*)\]/g, "'$1'")
    // Weekday names (must run before DD replacement)
    .replace(/dddd/g, 'EEEE')
    .replace(/ddd/g, 'EEE')
    // Year
    .replace(/YYYY/g, 'yyyy')
    .replace(/YY/g, 'yy')
    // Day of month (padded DD → dd, then unpadded D → d)
    .replace(/DD/g, 'dd')
    .replace(/(?<=[\s,/\-.('])D(?=[\s,/\-.)']|$)/g, 'd')
    // AM/PM marker: moment uses uppercase A, date-fns uses lowercase a
    .replace(/(?<![a-zA-Z])A(?![a-zA-Z])/g, 'a');
}

export function formatToLocalDate(input: string, formatStr: string, dateOnly?: boolean) {
  if (!input) return "";

  const dfnsFmt = momentFormatToDateFns(formatStr || "yyyy-MM-dd");

  // If dateOnly is passed, skip timezone conversions and only pick date part
  if (dateOnly) {
    // Extract just the date part (YYYY-MM-DD) from any datetime format
    const dateMatch = input.match(/^\d{4}-\d{2}-\d{2}/);
    if (!dateMatch) return "";

    const [year, month, day] = dateMatch[0].split('-').map(Number);
    const d = new Date(year, month - 1, day);
    if (!isValid(d)) return "";

    return format(d, dfnsFmt);
  }

  const zone = getTimezone();

  // Regex detects if input has timezone info (Z, ±hh:mm, ±hhmm)
  const hasOffset = /([+-]\d{2}:?\d{2}|Z)$/i.test(input);

  // Case 1: only date (YYYY-MM-DD)
  const isDateOnlyStr = /^\d{4}-\d{2}-\d{2}$/.test(input);

  let date: Date;

  if (isDateOnlyStr) {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = input.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    // Parse the input (handles ISO strings with and without offsets)
    const parsed = new Date(input);
    if (!isValid(parsed)) return "";
    // Convert to the target timezone using Intl trick
    date = new Date(parsed.toLocaleString('en-US', { timeZone: zone }));
  }

  if (!isValid(date)) return "";

  const finalFmt = dfnsFmt || (isDateOnlyStr ? "yyyy-MM-dd" : "yyyy-MM-dd HH:mm");
  return format(date, finalFmt);
}

export function getTimezone(): string {
  if (localStorage.getItem("userTimezone") && localStorage.getItem("userTimezone") !== null) {
    return localStorage.getItem("userTimezone") || "";
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function getTimeZonesList() {
  return Intl.supportedValuesOf('timeZone').map(zone => {
    // Get the UTC offset for this zone
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find(p => p.type === 'timeZoneName')?.value || '';

    // Parse the offset string (e.g., "GMT+5:30" or "GMT-8")
    const match = offsetPart.match(/GMT([+-]\d{1,2}(?::\d{2})?)?/);
    let sign = '+';
    let hours = '00';
    let minutes = '00';

    if (match && match[1]) {
      sign = match[1][0];
      const rest = match[1].slice(1);
      if (rest.includes(':')) {
        [hours, minutes] = rest.split(':');
        hours = hours.padStart(2, '0');
        minutes = minutes.padStart(2, '0');
      } else {
        hours = rest.padStart(2, '0');
      }
    }

    const text = `(GMT${sign}${hours}:${minutes}) ${zone.split("/").pop()?.replace("_", " ")}`;

    return {
      zone,
      text
    };
  });
}

/** Contact shape with optional is_default flag */
type ContactLike = { key?: string; value?: string; is_default?: boolean };

/**
 * Get the default contact value for a given key (e.g. 'email', 'phone').
 * Respects is_default flag: prefers the contact marked default, falls back to first match.
 */
export function getDefaultContactValue(contacts: ContactLike[] | undefined, key: string): string {
  if (!Array.isArray(contacts)) return '';
  const matchDefault = contacts.find(
    (c) => c?.key === key && (c?.is_default === true || c?.is_default === 'true') && c?.value
  );
  if (matchDefault) return matchDefault.value ?? '';
  const matchFirst = contacts.find((c) => c?.key === key && c?.value);
  return matchFirst?.value ?? '';
}

/**
 * Get default email and phone from contacts array.
 * Respects is_default flag when multiple contacts of same type exist.
 */
export function getDefaultEmailAndPhone(contacts: ContactLike[] | undefined): {
  email: string;
  phone: string;
} {
  return {
    email: getDefaultContactValue(contacts, 'email'),
    phone: getDefaultContactValue(contacts, 'phone'),
  };
}
